import {
  getPaginationRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  RowSelectionState,
  PaginationState,
  getCoreRowModel,
  useReactTable,
  SortingState,
  TableState,
  Row,
} from '@ui/components';
import { useCallback, useState, useMemo } from 'react';
import { ColumnDef } from '@ui/components';

export interface UseComboboxProps<T> {
  options: T[];
  phraseChange?: (phrase: string) => void;
  disabled?: boolean;
  isLoading?: boolean;
}

export function useCombobox<T>({
  phraseChange,
  isLoading,
  disabled,
  options,
}: UseComboboxProps<T>) {
  const [phrase, setPhrase] = useState('');
  const [selected, setSelected] = useState<string[]>([]);

  const setPhraseHandler = useCallback(
    (phrase: string) => {
      setPhrase(phrase);
      phraseChange?.(phrase);
    },
    [phraseChange, setPhrase],
  );

  return {
    setPhrase: setPhraseHandler,
    setSelected,
    isLoading,
    selected,
    disabled,
    options,
    phrase,
  };
}

export type UseCombobox<T> = ReturnType<typeof useCombobox<T>>;

export interface UseAssignTableProps<T> {
  onStateChange: (state: Partial<TableState>) => void;
  hideColumns?: Partial<Record<string, boolean>>;
  enableRowSelection?: (row: Row<T>) => boolean;
  phraseChange: (phrase: string) => void;
  initialState?: Partial<TableState>;
  getRowId: (row: T) => string;
  isOptionLoading?: boolean;
  columns: ColumnDef<T>[];
  isLoading?: boolean;
  totalItems: number;
  options: T[];
  data: T[];
}

export function useAssignTable<T>({
  enableRowSelection = () => true,
  isOptionLoading,
  onStateChange,
  initialState,
  phraseChange,
  hideColumns,
  totalItems,
  isLoading,
  getRowId,
  columns,
  options,
  data,
}: UseAssignTableProps<T>) {
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [pagination, setPagination] = useState<PaginationState>(
    initialState?.pagination ?? { pageIndex: 0, pageSize: 8 },
  );
  const [globalFilter, setGlobalFilter] = useState(initialState?.globalFilter ?? '');
  const [sorting, setSorting] = useState<SortingState>(initialState?.sorting ?? []);

  const columnVisibility = useMemo(() => {
    return columns.reduce(
      (acc, column) => {
        if (column.id) {
          return { ...acc, [column.id]: !hideColumns?.[column.id] };
        }
        return acc;
      },
      {} as Record<string, boolean>,
    );
  }, [columns, hideColumns]);

  const combobox = useCombobox<T>({
    isLoading: isOptionLoading ?? false,
    phraseChange,
    options,
  });

  const table = useReactTable({
    data: data ?? [],
    columns,
    state: { pagination, globalFilter, rowSelection, sorting, columnVisibility },
    pageCount: Math.ceil(totalItems / pagination.pageSize),
    rowCount: totalItems,
    manualPagination: true,
    manualFiltering: true,
    manualSorting: true,
    enableRowSelection,
    getRowId,
    getPaginationRowModel: getPaginationRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getCoreRowModel: getCoreRowModel(),
    onGlobalFilterChange: (updater) => {
      const newGlobalFilter = typeof updater === 'function' ? updater(globalFilter) : updater;
      onStateChange({ globalFilter: newGlobalFilter, pagination, rowSelection, sorting });
      setGlobalFilter(newGlobalFilter);
    },
    onRowSelectionChange: (updater) => {
      const newRowSelection = typeof updater === 'function' ? updater(rowSelection) : updater;
      onStateChange({ globalFilter, pagination, rowSelection: newRowSelection, sorting });
      setRowSelection(newRowSelection);
    },
    onPaginationChange: (updater) => {
      const newPagination = typeof updater === 'function' ? updater(pagination) : updater;
      onStateChange({ globalFilter, pagination: newPagination, rowSelection, sorting });
      setPagination(newPagination);
    },
    onSortingChange: (updater) => {
      const newSorting = typeof updater === 'function' ? updater(sorting) : updater;
      onStateChange({ globalFilter, pagination, rowSelection, sorting: newSorting });
      setSorting(newSorting);
    },
  });

  return {
    table,
    combobox,
    isLoading,
    getRowId,
  };
}

export type UseAssignTable<T> = ReturnType<typeof useAssignTable<T>>;
